import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { sections } from './Dashboard';
import Modal from './Modal';
import Login from './Login';
import {
  FaShieldAlt,
  FaChalkboardTeacher,
  FaCheckCircle,
  FaPhoneAlt,
  FaExpandAlt,
  FaCalendarAlt
} from 'react-icons/fa';
import '../style/EducationBlock.scss';

// Օգնող կոմպոնենտ տեքստի բացման/փակման համար
const ExpandableText = ({ text }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!text) return null;

  return (
    <>
      <p className={`bio-description ${isExpanded ? 'expanded' : 'collapsed'}`}>
        {text}
      </p>
      {text.length > 120 && (
        <button
          className="read-more-btn"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? 'Փակել' : 'Կարդալ ավելին'}
        </button>
      )}
    </>
  );
};

export default function EducationBlock() {
  const { sectionId } = useParams();
  const navigate = useNavigate();
  const { user, handleLogin } = useContext(AuthContext);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fullscreenMedia, setFullscreenMedia] = useState(null);
  const [addedContent, setAddedContent] = useState([]);
  const [dynamicTrainer, setDynamicTrainer] = useState({
    name: '', tel: '', pictures: ''
  });

  const currentSection = sections.find(s => s.id === sectionId);

  useEffect(() => {
    if (sectionId) {
      fetch(`/api/content/${sectionId}`)
        .then(res => res.json())
        .then(data => {
          if (data) {
            setDynamicTrainer({
              name: data.name || currentSection?.name,
              tel: data.tel || currentSection?.tel,
              pictures: data.pictures || currentSection?.pictures
            });
          }
        })
        .catch(err => console.error("Trainer fetch error:", err));

      fetch(`/api/content/list/${sectionId}`)
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data)) setAddedContent(data);
        })
        .catch(err => console.error("Content fetch error:", err));
    }
  }, [sectionId, currentSection]);

  if (!currentSection) return <div className="not-found">Բաժինը չի գտնվել</div>;

  const hasAccess = user && (user.access === 'all' || user.access === sectionId);
  const closeFullscreen = () => setFullscreenMedia(null);

  return (
    <div className="education-detail">
      <div className="content-card">
        <div className="bg-icon-watermark">{currentSection.icon}</div>

        <header className="page-header">
          <div className="badge-row">
            <span className="category-badge">{currentSection.title}</span>
            {!hasAccess && (
              <button className="admin-lock-btn" onClick={() => setIsModalOpen(true)}>
                <FaShieldAlt /> Ադմինիստրատոր
              </button>
            )}
          </div>
        </header>

        <div className="main-grid">
          <article className="description-box">
            <div className="slogan-box">
              <h3>{currentSection.slagon}</h3>
            </div>
            <p className="bio-text">{currentSection.bio}</p>

            <div className="why-us">
              <h4>Ինչո՞ւ ընտրել {currentSection.title}</h4>
              <hr />
              <ul className="benefit-list">
                <li><FaCheckCircle /> {currentSection.what}</li>
                <li><FaCheckCircle /> Պրակտիկ առաջադրանքներ և իրական նախագծեր</li>
                {currentSection.title === 'Վեբ ծրագրավորում' && (
                  <li><FaCheckCircle /> Կ.Գ.Մ.Ս նախարարության կողմից երաշխավորված ուսումնական ծրագիր</li>
                )}
                <li><FaCheckCircle /> Որակավորման հավաստագիր ավարտին</li>
              </ul>
            </div>
          </article>

          <aside className="trainer-sidebar">
            <div className="trainer-card">
              <div className="trainer-header">
                <FaChalkboardTeacher />
                <h4>Դասընթացավար</h4>
              </div>
              <div className="trainer-profile">
                <div className="img-frame" onClick={() => setFullscreenMedia({ type: 'image', url: dynamicTrainer.pictures })}>
                  <img src={dynamicTrainer.pictures} alt={dynamicTrainer.name} className="clickable-media" />
                  <div className="zoom-overlay"><FaExpandAlt /></div>
                </div>
                <h5>{dynamicTrainer.name}</h5>
                <p>Մասնագիտական ուղղորդման փորձառու վարպետ</p>
                <a href={`tel:${dynamicTrainer.tel}`} className="contact-link">
                  <FaPhoneAlt /> {dynamicTrainer.tel}
                </a>
              </div>
            </div>
          </aside>
        </div>

        <section className='content-list-section'>
          {addedContent.length > 0 && (
            <div className="section-title-wrapper">
              <h3 className="section-title">Դասընթացի նյութեր և մեդիա</h3>
              <div className="title-underline"></div>
            </div>
          )}
          <div className='added-content-grid'>
            {addedContent.map((item) => (
              <div key={item.id} className="dynamic-content-item">
                <div className="item-media-box">
                  {item.image && (
                    <img
                      src={item.image}
                      alt={item.title}
                      className="content-img clickable-media"
                      onClick={() => setFullscreenMedia({ type: 'image', url: item.image })}
                    />
                  )}
                  {item.video && (
                    <div className="video-wrapper" onClick={() => setFullscreenMedia({ type: 'video', url: item.video })}>
                      <video className="content-video-preview">
                        <source src={item.video} type="video/mp4" />
                      </video>
                      <div className="play-hint"><FaExpandAlt /></div>
                    </div>
                  )}
                </div>

                <div className="item-info">
                  {item.title && <h4>{item.title}</h4>}

                  {/* Օգտագործում ենք նոր ֆունկցիան այստեղ */}
                  <ExpandableText text={item.desc} />

                  <div className="item-meta">
                    <span className="post-date"><FaCalendarAlt /> {item.createdAt}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {hasAccess && (
          <footer className="admin-footer">
            <div className="admin-status">
              <span>🔧 Ադմինիստրատորի հասանելիություն միացված է ({user.role})</span>
              <button onClick={() => navigate('/admin-dashboard')}>Կառավարման վահանակ</button>
            </div>
          </footer>
        )}
      </div>

      {fullscreenMedia && (
        <div className="media-fullscreen-overlay" onClick={closeFullscreen}>
          <button className="close-fs" onClick={closeFullscreen}>&times;</button>
          <div className="fs-content-wrapper" onClick={(e) => e.stopPropagation()}>
            {fullscreenMedia.type === 'image' ? (
              <img src={fullscreenMedia.url} alt="Fullscreen" />
            ) : (
              <video controls autoPlay>
                <source src={fullscreenMedia.url} type="video/mp4" />
              </video>
            )}
          </div>
        </div>
      )}

      {isModalOpen && (
        <Modal onClose={() => setIsModalOpen(false)}>
          <Login onLogin={(data) => { handleLogin(data); setIsModalOpen(false); }} currentSectionId={sectionId} />
        </Modal>
      )}
    </div>
  );
}